import {
  ChildrenIcon,
  PointIcon,
  PrivacyIcon,
  SchoolIcon,
  TrendUpIcon,
} from '../../../shared/components'
import type { ActivityEvent } from '../../../shared/api/platform'

/**
 * F1's "Recent Activities" list.
 *
 * 🔴 WHAT THIS REPLACED WAS FIVE INVENTED SENTENCES. 'New institution
 * "Greenwood International School" added', 'Teacher "Riya Sharma" assigned 5
 * games to Class 2A', 'Payment received from "Sunrise Public School"', 'New game
 * "Lily Pad Count" published', 'System backup completed successfully' — with
 * "2 minutes ago", "15 minutes ago", "1 hour ago" beside them. Two named people
 * who do not exist, one described a payment nobody made, and one reported
 * infrastructure this product does not run. All five aged not at all: the card
 * said "2 minutes ago" on a dashboard left open overnight.
 *
 * ⚠️ THE AGE IS COMPUTED FROM THE EVENT'S OWN TIMESTAMP AT RENDER TIME, so it is
 * true whenever the card is looked at rather than whenever it was written.
 */

const TINT: Record<ActivityEvent['kind'], { ring: string; icon: React.ReactNode }> = {
  SCHOOL_ONBOARDED: {
    ring: 'bg-purple-100 text-purple-600',
    icon: <SchoolIcon size={14} />,
  },
  SUBSCRIPTION_STARTED: {
    ring: 'bg-emerald-100 text-emerald-600',
    icon: <TrendUpIcon size={14} />,
  },
  SUBSCRIPTION_CHANGED: {
    ring: 'bg-amber-100 text-amber-600',
    icon: <PointIcon size={14} />,
  },
  COUPON_CREATED: { ring: 'bg-blue-100 text-blue-600', icon: <PrivacyIcon size={14} /> },
  REQUEST_RECEIVED: { ring: 'bg-teal-100 text-teal-600', icon: <ChildrenIcon size={14} /> },
}

/**
 * How long ago, in words.
 *
 * 🔴 IT ROUNDS DOWN AND NEVER FORWARD. "in 2 minutes" from a clock a second
 * ahead of the server's would make the feed look broken; anything at or after
 * now is "just now".
 *
 * ⚠️ IT STOPS BEING RELATIVE AT A WEEK. "23 days ago" is arithmetic the reader
 * has to do to get back to a date; past a week the date itself is the useful
 * thing, and this card is about what happened lately anyway.
 */
export function timeAgo(at: string, now: Date = new Date()): string {
  const then = new Date(at)
  if (Number.isNaN(then.getTime())) return ''
  const secs = Math.floor((now.getTime() - then.getTime()) / 1000)
  if (secs < 60) return 'just now'
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${String(mins)} ${mins === 1 ? 'minute' : 'minutes'} ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${String(hours)} ${hours === 1 ? 'hour' : 'hours'} ago`
  const days = Math.floor(hours / 24)
  if (days <= 7) return `${String(days)} ${days === 1 ? 'day' : 'days'} ago`
  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

export interface ActivityFeedProps {
  events: readonly ActivityEvent[]
  /** Opens the school an event is about. Omitted, or a `null` schoolId, renders plain text. */
  onOpenSchool?: (schoolId: string) => void
}

export function ActivityFeed({ events, onOpenSchool }: ActivityFeedProps) {
  /*
   * 🔴 AN EMPTY PLATFORM GETS A SENTENCE, NOT FILLER. The card this replaced had
   * no empty state because it had no data — it showed five fictional events on a
   * platform with nothing on it, which is the most misleading moment it could
   * possibly have chosen to be confident.
   */
  if (events.length === 0) {
    return (
      <p className="flex h-40 items-center justify-center px-4 text-center font-dash text-dash-xs font-semibold text-muted">
        Nothing has happened on the platform yet. Onboardings, plan changes,
        coupons and requests appear here.
      </p>
    )
  }

  return (
    <ul className="my-auto flex flex-col gap-3.5">
      {events.map((e) => {
        const tint = TINT[e.kind]
        const openable = e.schoolId !== null && onOpenSchool !== undefined
        return (
          <li key={e.id} className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tint.ring}`}
            >
              {tint.icon}
            </div>
            <div className="min-w-0">
              {openable ? (
                <button
                  type="button"
                  onClick={() => {
                    onOpenSchool(e.schoolId as string)
                  }}
                  className="cursor-pointer text-left text-dash-xs font-bold text-ink hover:underline"
                >
                  {e.summary}
                </button>
              ) : (
                <p className="text-dash-xs font-bold text-ink">{e.summary}</p>
              )}
              {/*
                ⚠️ THE EXACT INSTANT IS IN THE TOOLTIP. "3 days ago" is the right
                thing to read at a glance and the wrong thing to quote in a
                support thread; `title` keeps both without a second line.
              */}
              <span
                className="mt-0.5 block text-[11px] text-muted"
                title={new Date(e.at).toLocaleString()}
              >
                {timeAgo(e.at)}
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
